/**
 * Settings navigation visibility rules for the left rail.
 *
 * **`accessCenter` LaunchDarkly flag is off (legacy)** — show Settings only if **brand setup** or
 * **customer support** LD flag is explicitly `true` (`undefined` while loading counts as off).
 *
 * **`accessCenter` is on** — show Settings if **any** of:
 * - Access Centre permission path is satisfied (`accessCentreEligible`), or
 * - AI Brand Tools flag is on **and** brand AI permission path (`aiBrandEligible`), or
 * - Customer Support LD flag is on (no separate permission).
 *
 * **`contentOnlyMode`** — hide Settings when Access Centre LD is off (brand/support are not in
 * content-only). When Access Centre LD is on, show only when `accessCentreEligible` (Access Centre
 * permission + `/me` ready).
 */

/** `true` only when LaunchDarkly has finished loading and the flag is enabled (not `undefined`). */
const launchDarklyFlagIsOn = (flag: boolean | undefined): boolean => flag === true;

/**
 * Flag-only check when the Access Centre LaunchDarkly flag is **off** (legacy tenants).
 * Brand setup or customer support must be on; Access Centre flag is not used here.
 */
export const shouldShowSettingsNavForFlags = (
    aiBrandToolsFlag: boolean | undefined,
    customerSupportFlag: boolean | undefined
): boolean => launchDarklyFlagIsOn(aiBrandToolsFlag) || launchDarklyFlagIsOn(customerSupportFlag);

export type ShouldShowSettingsNavParams = {
    accessCenterFlag: boolean | undefined;
    aiBrandToolsFlag: boolean | undefined;
    customerSupportFlag: boolean | undefined;
    /**
     * When `accessCenterFlag` is `true`: user can open Settings for Access Centre (Users/Roles, etc.)
     * only if this is true. Typically `accessCentreNavReady && canViewAccessManagement` from `/me`.
     */
    accessCentreEligible: boolean;
    /**
     * Content-only tenants: show Settings only for Access Centre (users/roles), not brand or customer support.
     */
    contentOnlyMode?: boolean;
    /**
     * When `accessCenterFlag` is `true`: user may use AI Brand settings path only if this is true.
     * Typically `accessCentreNavReady && aiBrandToolsFlag && canRegisterAiBrandVoice`. Ignored when AC flag is off.
     */
    aiBrandEligible: boolean;
};

/**
 * Whether to render the Settings item in the navigation panel.
 * Pure function — safe to unit test without React or LaunchDarkly mocks.
 */
export const shouldShowSettingsNav = ({
    accessCenterFlag,
    aiBrandToolsFlag,
    customerSupportFlag,
    accessCentreEligible,
    contentOnlyMode = false,
    aiBrandEligible,
}: ShouldShowSettingsNavParams): boolean => {
    if (contentOnlyMode) {
        if (accessCenterFlag === true) {
            return accessCentreEligible;
        }
        return false;
    }

    if (accessCenterFlag !== true) {
        return shouldShowSettingsNavForFlags(aiBrandToolsFlag, customerSupportFlag);
    }

    const viaAccessCentre = accessCentreEligible;
    const viaAiBrand = aiBrandEligible;
    const viaCustomerSupport = launchDarklyFlagIsOn(customerSupportFlag);

    return viaAccessCentre || viaAiBrand || viaCustomerSupport;
};
